import React, { useEffect, useRef } from "react";

const resizeCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
) => {
  const { width, height } = canvas.getBoundingClientRect();
  const ratio = Math.ceil(window.devicePixelRatio);
  canvas.width = width * ratio;
  canvas.height = height * ratio;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
};

export type TDrawFunction = (
  context: CanvasRenderingContext2D,
  frameCount: number,
) => void;

interface ICanvasProps {
  draw: TDrawFunction;
  width: number;
  height: number;
}

export const Canvas = (props: ICanvasProps) => {
  const { draw, width, height } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    let frameCount = 0;
    let animationFrameId: number;
    const render = () => {
      ctx.save();
      resizeCanvas(canvas, ctx);
      draw(ctx, frameCount);
      frameCount++;
      ctx.restore();
      animationFrameId = window.requestAnimationFrame(render);
    };
    render();
    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [draw]);

  return <canvas ref={canvasRef} width={width} height={height} />;
};

export type TStaticDrawFunction = (context: CanvasRenderingContext2D) => void;

interface IStaticCanvasProps {
  draw: TStaticDrawFunction;
  width: number;
  height: number;
}

export const StaticCanvas = (props: IStaticCanvasProps) => {
  const { draw, width, height } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.save();
    resizeCanvas(canvas, ctx);
    draw(ctx);
    ctx.restore();
  }, [draw]);

  return <canvas ref={canvasRef} width={width} height={height} />;
};
